import { format, isValid } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { Control, Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { dateStringToDateObject } from 'utils/parseDate';
import { DatePickerField } from '../common/DatePickerField';

const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
/** What an entry the picker cannot read is stored as: not empty, so the field's rules see it, and unparseable. */
const UNREADABLE = 'Invalid Date';

/** The stored string for a picked value: `''` once cleared, {@link UNREADABLE} while the entry is incomplete. */
export function toDateTimeString(value: Date | null): string {
    if (!value) {
        return '';
    }
    return isValid(value) ? format(value, DATE_TIME_FORMAT) : UNREADABLE;
}

/**
 * The picker value for a stored string. An unreadable one gives an invalid `Date`, which the picker takes as the
 * entry it is still holding rather than as a new value that would wipe what was typed.
 */
export function toPickerValue(value: string | null | undefined): Date | null {
    return value ? dateStringToDateObject(value, DATE_TIME_FORMAT) : null;
}

interface DateTimeInputProps<T extends FieldValues, N extends Path<T>> {
    control: Control<T>;
    name: N;
    rules?: UseControllerProps<T, N>['rules'];
    disabled?: boolean;
    placeholder?: string;
}

/**
 * A date and minute picker bound to a `yyyy-MM-dd HH:mm` string with no time zone, as `DateInput` is bound to
 * `yyyy-MM-dd`: the wall clock picked is the wall clock stored, never shifted by the viewer's offset. It shows the
 * tenant's date format and ids its input by the field name. The error message is the caller's to show.
 *
 * ponytail: the string goes through a local `Date`, so a wall clock inside the viewer's own spring-forward gap
 * (one hour a year) shows an hour later, and is stored so once the field is edited. Reading and writing the string
 * as UTC removes that.
 */
export default function DateTimeInput<T extends FieldValues, N extends Path<T>>({
    control,
    name,
    rules,
    disabled,
    placeholder,
}: DateTimeInputProps<T, N>) {
    const dateFormat = useEffectiveDateFormat();

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <DatePickerField
                    mode="datetime"
                    format={`${dateFormat} HH:mm`}
                    minuteStep={1}
                    id={name}
                    inputRef={field.ref}
                    value={toPickerValue(field.value)}
                    onChange={(value) => field.onChange(toDateTimeString(value))}
                    disabled={disabled}
                    invalid={error ? true : undefined}
                    // No "Today": it picks the viewer's local clock, which a caller storing UTC would misread.
                    actions={['clear', 'accept']}
                    clearable={false}
                    placeholder={placeholder}
                />
            )}
        />
    );
}
